import { PlusIconV2 } from '../resources/svgComponents/PlusIconV2';
import { MinusIconV2 } from '../resources/svgComponents/MinusIconV2';
import { XIconV2 } from '../resources/svgComponents/XIconV2';
import { DivideIconV2 } from '../resources/svgComponents/DivideIconV2';
import { DivideBackground } from '../resources/svgComponents/DivideBackground';
import { MinusBackground } from '../resources/svgComponents/MinusBackgroud';
import { MultiplyBackground } from '../resources/svgComponents/MultiplyBackground';
import { PlusBackground } from '../resources/svgComponents/PlusBackground';
import { AIDifficultyLevel } from '../components/screens/PlayWithAI/useJoinAiGameMutation.gql';

export enum UTIL_QUERY_KEYS {
  serverVersion = 'serverVersion',
  minimumMobileJSVersion = 'minimumMobileJSVersion',
}

export enum GAME_LINK_TYPE {
  GYM_ASSESSMENT = 'GYM_ASSESSMENT',
  JOURNEY = 'JOURNEY',
  AI = 'AI',
}

// Use a plain object for mapping labels
export const E_AI_DIFFICULTY_LEVEL_LABEL: Record<AIDifficultyLevel, string> = {
  [AIDifficultyLevel.EASY]: 'Mild',
  [AIDifficultyLevel.MEDIUM]: 'Medium',
  [AIDifficultyLevel.HARD]: 'Spicy',
  [AIDifficultyLevel.EXTREME]: 'Extreme',
};

export enum GAME_OPERATION_TYPE {
  ADDITION = 'Addition',
  SUBTRACTION = 'Subtraction',
  MULTIPLICATION = 'Multiplication',
  DIVISION = 'Division',
}

export enum GAME_TYPES_TYPE {
  MULTI_1_12 = 'MULTI_1_12',
  MULTI_1_10 = 'MULTI_1_10',
  MULTI_MINUS_6_6 = 'MULTI_MINUS_6_6',
  MULTI_ALGEBRA = 'MULTI_ALGEBRA',
  MULTI_ALGEBRA_2_VAR = 'MULTI_ALGEBRA_2_VAR',
  MULTI_ALGEBRA_EXPONENTS = 'MULTI_ALGEBRA_EXPONENTS',
  MULTI_FACT_GROUP_0_1_5_10 = 'MULTI_FACT_GROUP_0_1_5_10',
  MULTI_FACT_GROUP_1_2_4_8 = 'MULTI_FACT_GROUP_1_2_4_8',
  MULTI_FACT_GROUP_3_6_12 = 'MULTI_FACT_GROUP_3_6_12',
  MULTI_FACT_GROUP_7_9_11 = 'MULTI_FACT_GROUP_7_9_11',
  ADD_1_12 = 'ADD_1_12',
  ADD_MINUS_6_6 = 'ADD_MINUS_6_6',
  ADD_ALGEBRA = 'ADD_ALGEBRA',
  SUBTRACT_POSITIVE_INTEGERS = 'SUBTRACT_POSITIVE_INTEGERS',
  SUBTRACT_INTEGERS = 'SUBTRACT_INTEGERS',
  DIVIDE = 'DIVIDE',
}

export const GAME_TYPE_ARRANGEMENT: GAME_TYPES_TYPE[] = [
  GAME_TYPES_TYPE.ADD_1_12,
  GAME_TYPES_TYPE.ADD_MINUS_6_6,
  GAME_TYPES_TYPE.ADD_ALGEBRA,
  GAME_TYPES_TYPE.SUBTRACT_POSITIVE_INTEGERS,
  GAME_TYPES_TYPE.SUBTRACT_INTEGERS,
  GAME_TYPES_TYPE.DIVIDE,
  GAME_TYPES_TYPE.MULTI_FACT_GROUP_0_1_5_10,
  GAME_TYPES_TYPE.MULTI_FACT_GROUP_1_2_4_8,
  GAME_TYPES_TYPE.MULTI_FACT_GROUP_3_6_12,
  GAME_TYPES_TYPE.MULTI_FACT_GROUP_7_9_11,
  GAME_TYPES_TYPE.MULTI_1_10,
  GAME_TYPES_TYPE.MULTI_1_12,
  GAME_TYPES_TYPE.MULTI_MINUS_6_6,
  GAME_TYPES_TYPE.MULTI_ALGEBRA,
  GAME_TYPES_TYPE.MULTI_ALGEBRA_2_VAR,
  GAME_TYPES_TYPE.MULTI_ALGEBRA_EXPONENTS,
];

export enum SLIDER_POSITION {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
}

// We are trying to get any ax + b in the operands, and x value is the power of x in the equation.
export interface OPERAND_TYPE {
  a: number;
  x: number;
  b: number;
  c?: number | null; // For algebra, where we have ax + by = c
  y?: number | null; // For algebra, where we have ax + by = c
}

export type TAnswerPair = {
  left: OPERAND_TYPE;
  right: OPERAND_TYPE;
};

export type TAnswerRow = {
  answer: OPERAND_TYPE;
  pairs: TAnswerPair[];
};

export interface IConfigBase {
  maxInARow: number;
  rowsCount: number[];
  hasDuplicate: boolean;
  hasHint: boolean;
  howToWin: string;
  hasWorkingBar: boolean;
  name: string;
  leftOperandRowButtonCount?: number;
  rightOperandRowButtonCount?: number;
  operation: GAME_OPERATION_TYPE;
}

export type TConfig = IConfigBase &
  ({ operands: OPERAND_TYPE[] } | { leftOperands: OPERAND_TYPE[]; rightOperands: OPERAND_TYPE[] });

export const GAME_TYPES_CONFIG: Record<GAME_TYPES_TYPE, TConfig> = {
  [GAME_TYPES_TYPE.MULTI_1_12]: {
    maxInARow: 8,
    rowsCount: [5, 6, 7, 8, 7, 8, 7, 6, 5],
    operands: [
      { a: 1, x: 0, c: null, y: null, b: 0 },
      { a: 2, x: 0, c: null, y: null, b: 0 },
      { a: 3, x: 0, c: null, y: null, b: 0 },
      { a: 4, x: 0, c: null, y: null, b: 0 },
      { a: 5, x: 0, c: null, y: null, b: 0 },
      { a: 6, x: 0, c: null, y: null, b: 0 },
      { a: 7, x: 0, c: null, y: null, b: 0 },
      { a: 8, x: 0, c: null, y: null, b: 0 },
      { a: 9, x: 0, c: null, y: null, b: 0 },
      { a: 10, x: 0, c: null, y: null, b: 0 },
      { a: 11, x: 0, c: null, y: null, b: 0 },
      { a: 12, x: 0, c: null, y: null, b: 0 },
    ],
    hasDuplicate: false,
    hasHint: true,
    howToWin: 'Get 4 in a row by multiplying',
    hasWorkingBar: true,
    name: '1-12',
    operation: GAME_OPERATION_TYPE.MULTIPLICATION,
  },
  [GAME_TYPES_TYPE.MULTI_1_10]: {
    maxInARow: 7,
    rowsCount: [2, 3, 4, 5, 6, 7, 6, 5, 4],
    operands: [
      { a: 1, x: 0, c: null, y: null, b: 0 },
      { a: 2, x: 0, c: null, y: null, b: 0 },
      { a: 3, x: 0, c: null, y: null, b: 0 },
      { a: 4, x: 0, c: null, y: null, b: 0 },
      { a: 5, x: 0, c: null, y: null, b: 0 },
      { a: 6, x: 0, c: null, y: null, b: 0 },
      { a: 7, x: 0, c: null, y: null, b: 0 },
      { a: 8, x: 0, c: null, y: null, b: 0 },
      { a: 9, x: 0, c: null, y: null, b: 0 },
      { a: 10, x: 0, c: null, y: null, b: 0 },
    ],
    hasDuplicate: false,
    hasHint: true,
    howToWin: 'Get 4 in a row by multiplying',
    hasWorkingBar: true,
    name: '1-10',
    operation: GAME_OPERATION_TYPE.MULTIPLICATION,
  },
  [GAME_TYPES_TYPE.ADD_1_12]: {
    maxInARow: 8,
    rowsCount: [3, 4, 5, 6, 7, 8, 7, 6],
    operands: [
      { a: 1, x: 0, c: null, y: null, b: 0 },
      { a: 2, x: 0, c: null, y: null, b: 0 },
      { a: 3, x: 0, c: null, y: null, b: 0 },
      { a: 4, x: 0, c: null, y: null, b: 0 },
      { a: 5, x: 0, c: null, y: null, b: 0 },
      { a: 6, x: 0, c: null, y: null, b: 0 },
      { a: 7, x: 0, c: null, y: null, b: 0 },
      { a: 8, x: 0, c: null, y: null, b: 0 },
      { a: 9, x: 0, c: null, y: null, b: 0 },
      { a: 10, x: 0, c: null, y: null, b: 0 },
      { a: 11, x: 0, c: null, y: null, b: 0 },
      { a: 12, x: 0, c: null, y: null, b: 0 },
    ],
    hasDuplicate: true,
    hasHint: false,
    howToWin: 'Get 4 in a row by adding',
    hasWorkingBar: true,
    name: '1-12',
    operation: GAME_OPERATION_TYPE.ADDITION,
  },
  [GAME_TYPES_TYPE.ADD_MINUS_6_6]: {
    maxInARow: 7,
    rowsCount: [5, 6, 7, 6, 7, 6, 7, 6],
    operands: [
      { a: -6, x: 0, c: null, y: null, b: 0 },
      { a: -5, x: 0, c: null, y: null, b: 0 },
      { a: -4, x: 0, c: null, y: null, b: 0 },
      { a: -3, x: 0, c: null, y: null, b: 0 },
      { a: -2, x: 0, c: null, y: null, b: 0 },
      { a: -1, x: 0, c: null, y: null, b: 0 },
      { a: 1, x: 0, c: null, y: null, b: 0 },
      { a: 2, x: 0, c: null, y: null, b: 0 },
      { a: 3, x: 0, c: null, y: null, b: 0 },
      { a: 4, x: 0, c: null, y: null, b: 0 },
      { a: 5, x: 0, c: null, y: null, b: 0 },
      { a: 6, x: 0, c: null, y: null, b: 0 },
    ],
    hasDuplicate: true,
    hasHint: false,
    howToWin: 'Get 4 in a row by adding',
    hasWorkingBar: false,
    name: '-6 to +6',
    operation: GAME_OPERATION_TYPE.ADDITION,
  },
  [GAME_TYPES_TYPE.ADD_ALGEBRA]: {
    maxInARow: 7,
    rowsCount: [5, 6, 7, 6, 7, 6, 5],
    // ax +cy +b
    operands: [
      { a: 0, x: 0, c: 0, y: 0, b: 1 },
      { a: 0, x: 0, c: 0, y: 0, b: 2 },
      { a: 0, x: 0, c: 0, y: 0, b: 3 },
      { a: 1, x: 1, c: 0, y: 0, b: 0 },
      { a: 2, x: 1, c: 0, y: 0, b: 0 },
      { a: 3, x: 1, c: 0, y: 0, b: 0 },
      { a: 0, x: 0, c: 1, y: 1, b: 0 },
      { a: 0, x: 0, c: 2, y: 1, b: 0 },
      { a: 0, x: 0, c: 3, y: 1, b: 0 },
    ],
    hasDuplicate: true,
    hasHint: false,
    howToWin: 'Get 4 in a row by adding',
    hasWorkingBar: false,
    name: 'Algebra',
    leftOperandRowButtonCount: 3,
    rightOperandRowButtonCount: 3,
    operation: GAME_OPERATION_TYPE.ADDITION,
  },
  [GAME_TYPES_TYPE.MULTI_MINUS_6_6]: {
    maxInARow: 7,
    rowsCount: [3, 4, 5, 6, 7, 6, 5],
    operands: [
      { a: -6, x: 0, c: null, y: null, b: 0 },
      { a: -5, x: 0, c: null, y: null, b: 0 },
      { a: -4, x: 0, c: null, y: null, b: 0 },
      { a: -3, x: 0, c: null, y: null, b: 0 },
      { a: -2, x: 0, c: null, y: null, b: 0 },
      { a: -1, x: 0, c: null, y: null, b: 0 },
      { a: 1, x: 0, c: null, y: null, b: 0 },
      { a: 2, x: 0, c: null, y: null, b: 0 },
      { a: 3, x: 0, c: null, y: null, b: 0 },
      { a: 4, x: 0, c: null, y: null, b: 0 },
      { a: 5, x: 0, c: null, y: null, b: 0 },
      { a: 6, x: 0, c: null, y: null, b: 0 },
    ],
    hasDuplicate: false,
    hasHint: true,
    howToWin: 'Get 4 in a row by multiplying',
    hasWorkingBar: false,
    name: '-6 to +6',
    operation: GAME_OPERATION_TYPE.MULTIPLICATION,
  },
  [GAME_TYPES_TYPE.MULTI_ALGEBRA]: {
    maxInARow: 8,
    rowsCount: [5, 6, 7, 8, 7, 8, 7, 6],
    operands: [
      { a: 1, x: 0, c: null, y: null, b: 0 },
      { a: 2, x: 0, c: null, y: null, b: 0 },
      { a: 3, x: 0, c: null, y: null, b: 0 },
      { a: 4, x: 0, c: null, y: null, b: 0 },
      { a: 5, x: 0, c: null, y: null, b: 0 },
      { a: 6, x: 0, c: null, y: null, b: 0 },
      { a: 1, x: 1, c: null, y: null, b: 0 },
      { a: 2, x: 1, c: null, y: null, b: 0 },
      { a: 3, x: 1, c: null, y: null, b: 0 },
      { a: 4, x: 1, c: null, y: null, b: 0 },
      { a: 5, x: 1, c: null, y: null, b: 0 },
      { a: 6, x: 1, c: null, y: null, b: 0 },
    ],
    hasDuplicate: false,
    hasHint: true,
    howToWin: 'Get 4 in a row by multiplying',
    hasWorkingBar: false,
    name: 'Algebra (1 Variable)',
    operation: GAME_OPERATION_TYPE.MULTIPLICATION,
  },
  [GAME_TYPES_TYPE.MULTI_ALGEBRA_EXPONENTS]: {
    maxInARow: 7,
    rowsCount: [2, 3, 4, 5, 6, 7, 6, 5, 4, 3],
    // bax
    operands: [
      { a: 1, x: 1, c: 0, y: 0, b: 1 },
      { a: 1, x: 1, c: 0, y: 0, b: 2 },
      { a: 1, x: 1, c: 0, y: 0, b: 3 },
      { a: 1, x: 1, c: 0, y: 0, b: 4 },
      { a: 1, x: 2, c: 0, y: 0, b: 1 },
      { a: 1, x: 2, c: 0, y: 0, b: 2 },
      { a: 1, x: 2, c: 0, y: 0, b: 3 },
      { a: 1, x: 2, c: 0, y: 0, b: 4 },
      { a: 1, x: 3, c: 0, y: 0, b: 1 },
      { a: 1, x: 3, c: 0, y: 0, b: 2 },
      { a: 1, x: 3, c: 0, y: 0, b: 3 },
      { a: 1, x: 3, c: 0, y: 0, b: 4 },
    ],
    hasDuplicate: false,
    hasHint: false,
    howToWin: 'Get 4 in a row by multiplying',
    hasWorkingBar: false,
    name: 'Algebra (Exponents)',
    operation: GAME_OPERATION_TYPE.MULTIPLICATION,
  },
  [GAME_TYPES_TYPE.SUBTRACT_INTEGERS]: {
    maxInARow: 8,
    rowsCount: [3, 4, 5, 6, 7, 8, 7, 6],
    operands: [
      { a: 1, x: 0, c: 0, y: 0, b: 0 },
      { a: 2, x: 0, c: 0, y: 0, b: 0 },
      { a: 3, x: 0, c: 0, y: 0, b: 0 },
      { a: 4, x: 0, c: 0, y: 0, b: 0 },
      { a: 5, x: 0, c: 0, y: 0, b: 0 },
      { a: 6, x: 0, c: 0, y: 0, b: 0 },
      { a: 7, x: 0, c: 0, y: 0, b: 0 },
      { a: 8, x: 0, c: 0, y: 0, b: 0 },
      { a: 9, x: 0, c: 0, y: 0, b: 0 },
      { a: 10, x: 0, c: 0, y: 0, b: 0 },
      { a: 11, x: 0, c: 0, y: 0, b: 0 },
      { a: 12, x: 0, c: 0, y: 0, b: 0 },
    ],
    hasDuplicate: true,
    hasHint: false,
    howToWin: 'Get 4 in a row by subtracting',
    hasWorkingBar: false,
    name: '1-12',
    operation: GAME_OPERATION_TYPE.SUBTRACTION,
  },
  [GAME_TYPES_TYPE.SUBTRACT_POSITIVE_INTEGERS]: {
    maxInARow: 7,
    rowsCount: [4, 5, 6, 7, 6, 7, 6, 5],
    leftOperands: [
      { a: 13, x: 0, c: 0, y: 0, b: 0 },
      { a: 14, x: 0, c: 0, y: 0, b: 0 },
      { a: 15, x: 0, c: 0, y: 0, b: 0 },
      { a: 16, x: 0, c: 0, y: 0, b: 0 },
      { a: 17, x: 0, c: 0, y: 0, b: 0 },
      { a: 18, x: 0, c: 0, y: 0, b: 0 },
      { a: 19, x: 0, c: 0, y: 0, b: 0 },
      { a: 20, x: 0, c: 0, y: 0, b: 0 },
      { a: 21, x: 0, c: 0, y: 0, b: 0 },
      { a: 22, x: 0, c: 0, y: 0, b: 0 },
      { a: 23, x: 0, c: 0, y: 0, b: 0 },
      { a: 24, x: 0, c: 0, y: 0, b: 0 },
    ],
    rightOperands: [
      { a: 1, x: 0, c: 0, y: 0, b: 0 },
      { a: 2, x: 0, c: 0, y: 0, b: 0 },
      { a: 3, x: 0, c: 0, y: 0, b: 0 },
      { a: 4, x: 0, c: 0, y: 0, b: 0 },
      { a: 5, x: 0, c: 0, y: 0, b: 0 },
      { a: 6, x: 0, c: 0, y: 0, b: 0 },
      { a: 7, x: 0, c: 0, y: 0, b: 0 },
      { a: 8, x: 0, c: 0, y: 0, b: 0 },
      { a: 9, x: 0, c: 0, y: 0, b: 0 },
      { a: 10, x: 0, c: 0, y: 0, b: 0 },
      { a: 11, x: 0, c: 0, y: 0, b: 0 },
      { a: 12, x: 0, c: 0, y: 0, b: 0 },
    ],
    hasDuplicate: true,
    hasHint: false,
    howToWin: 'Get 4 in a row by subtracting',
    hasWorkingBar: false,
    name: 'Positive Integers',
    operation: GAME_OPERATION_TYPE.SUBTRACTION,
  },
  [GAME_TYPES_TYPE.DIVIDE]: {
    maxInARow: 6,
    rowsCount: [6, 5, 6, 5, 6, 5, 6],
    leftOperands: [
      { a: 12, x: 0, c: 0, y: 0, b: 0 },
      { a: 24, x: 0, c: 0, y: 0, b: 0 },
      { a: 36, x: 0, c: 0, y: 0, b: 0 },
      { a: 48, x: 0, c: 0, y: 0, b: 0 },
    ],
    rightOperands: [
      { a: 1, x: 0, c: 0, y: 0, b: 0 },
      { a: 2, x: 0, c: 0, y: 0, b: 0 },
      { a: 3, x: 0, c: 0, y: 0, b: 0 },
      { a: 4, x: 0, c: 0, y: 0, b: 0 },
      { a: 6, x: 0, c: 0, y: 0, b: 0 },
      { a: 12, x: 0, c: 0, y: 0, b: 0 },
    ],
    hasDuplicate: true,
    hasHint: false,
    howToWin: 'Get 4 in a row by dividing',
    hasWorkingBar: false,
    name: 'Positive Integers',
    rightOperandRowButtonCount: 3,
    operation: GAME_OPERATION_TYPE.DIVISION,
  },
  [GAME_TYPES_TYPE.MULTI_ALGEBRA_2_VAR]: {
    maxInARow: 8,
    rowsCount: [5, 6, 7, 8, 7, 8, 7, 6],
    // bac(xy)
    operands: [
      { a: 0, x: 0, c: 0, y: 0, b: 1 },
      { a: 0, x: 0, c: 0, y: 0, b: 2 },
      { a: 0, x: 0, c: 0, y: 0, b: 3 },
      { a: 0, x: 0, c: 0, y: 0, b: 4 },
      { a: 1, x: 1, c: 0, y: 0, b: 0 },
      { a: 2, x: 1, c: 0, y: 0, b: 0 },
      { a: 3, x: 1, c: 0, y: 0, b: 0 },
      { a: 4, x: 1, c: 0, y: 0, b: 0 },
      { a: 0, x: 0, c: 1, y: 1, b: 0 },
      { a: 0, x: 0, c: 2, y: 1, b: 0 },
      { a: 0, x: 0, c: 3, y: 1, b: 0 },
      { a: 0, x: 0, c: 4, y: 1, b: 0 },
    ],
    hasDuplicate: true,
    hasHint: false,
    howToWin: 'Get 4 in a row by multiplying',
    hasWorkingBar: false,
    name: 'Algebra (2 variables)',
    operation: GAME_OPERATION_TYPE.MULTIPLICATION,
  },
  [GAME_TYPES_TYPE.MULTI_FACT_GROUP_0_1_5_10]: {
    maxInARow: 6,
    rowsCount: [2, 3, 4, 5, 6, 5, 4],
    leftOperands: [
      { a: 0, x: 0, c: 0, y: 0, b: 0 },
      { a: 0, x: 0, c: 0, y: 0, b: 1 },
      { a: 0, x: 0, c: 0, y: 0, b: 5 },
      { a: 0, x: 0, c: 0, y: 0, b: 10 },
    ],
    rightOperands: [
      { a: 0, x: 0, c: 0, y: 0, b: 1 },
      { a: 0, x: 0, c: 0, y: 0, b: 2 },
      { a: 0, x: 0, c: 0, y: 0, b: 3 },
      { a: 0, x: 0, c: 0, y: 0, b: 4 },
      { a: 0, x: 0, c: 0, y: 0, b: 5 },
      { a: 0, x: 0, c: 0, y: 0, b: 6 },
      { a: 0, x: 0, c: 0, y: 0, b: 7 },
      { a: 0, x: 0, c: 0, y: 0, b: 8 },
      { a: 0, x: 0, c: 0, y: 0, b: 9 },
      { a: 0, x: 0, c: 0, y: 0, b: 10 },
      { a: 0, x: 0, c: 0, y: 0, b: 11 },
      { a: 0, x: 0, c: 0, y: 0, b: 12 },
    ],
    hasDuplicate: false,
    hasHint: false,
    howToWin: 'Get 4 in a row by multiplying',
    hasWorkingBar: false,
    name: '0,1,5,10 x 1-12',
    operation: GAME_OPERATION_TYPE.MULTIPLICATION,
  },
  [GAME_TYPES_TYPE.MULTI_FACT_GROUP_1_2_4_8]: {
    maxInARow: 6,
    rowsCount: [3, 4, 5, 6, 5, 4, 3],
    leftOperands: [
      { a: 0, x: 0, c: 0, y: 0, b: 1 },
      { a: 0, x: 0, c: 0, y: 0, b: 2 },
      { a: 0, x: 0, c: 0, y: 0, b: 4 },
      { a: 0, x: 0, c: 0, y: 0, b: 8 },
    ],
    rightOperands: [
      { a: 0, x: 0, c: 0, y: 0, b: 1 },
      { a: 0, x: 0, c: 0, y: 0, b: 2 },
      { a: 0, x: 0, c: 0, y: 0, b: 3 },
      { a: 0, x: 0, c: 0, y: 0, b: 4 },
      { a: 0, x: 0, c: 0, y: 0, b: 5 },
      { a: 0, x: 0, c: 0, y: 0, b: 6 },
      { a: 0, x: 0, c: 0, y: 0, b: 7 },
      { a: 0, x: 0, c: 0, y: 0, b: 8 },
      { a: 0, x: 0, c: 0, y: 0, b: 9 },
      { a: 0, x: 0, c: 0, y: 0, b: 10 },
      { a: 0, x: 0, c: 0, y: 0, b: 11 },
      { a: 0, x: 0, c: 0, y: 0, b: 12 },
    ],
    hasDuplicate: false,
    hasHint: false,
    howToWin: 'Get 4 in a row by multiplying',
    hasWorkingBar: false,
    name: '1,2,4,8 x 1-12',
    operation: GAME_OPERATION_TYPE.MULTIPLICATION,
  },
  [GAME_TYPES_TYPE.MULTI_FACT_GROUP_3_6_12]: {
    maxInARow: 6,
    rowsCount: [4, 5, 6, 5, 4],
    leftOperands: [
      { a: 0, x: 0, c: 0, y: 0, b: 3 },
      { a: 0, x: 0, c: 0, y: 0, b: 6 },
      { a: 0, x: 0, c: 0, y: 0, b: 12 },
    ],
    rightOperands: [
      { a: 0, x: 0, c: 0, y: 0, b: 1 },
      { a: 0, x: 0, c: 0, y: 0, b: 2 },
      { a: 0, x: 0, c: 0, y: 0, b: 3 },
      { a: 0, x: 0, c: 0, y: 0, b: 4 },
      { a: 0, x: 0, c: 0, y: 0, b: 5 },
      { a: 0, x: 0, c: 0, y: 0, b: 6 },
      { a: 0, x: 0, c: 0, y: 0, b: 7 },
      { a: 0, x: 0, c: 0, y: 0, b: 8 },
      { a: 0, x: 0, c: 0, y: 0, b: 9 },
      { a: 0, x: 0, c: 0, y: 0, b: 10 },
      { a: 0, x: 0, c: 0, y: 0, b: 11 },
      { a: 0, x: 0, c: 0, y: 0, b: 12 },
    ],
    hasDuplicate: false,
    hasHint: false,
    howToWin: 'Get 4 in a row by multiplying',
    hasWorkingBar: false,
    name: '3,6,12 x 1-12',
    leftOperandRowButtonCount: 3,
    operation: GAME_OPERATION_TYPE.MULTIPLICATION,
  },
  [GAME_TYPES_TYPE.MULTI_FACT_GROUP_7_9_11]: {
    maxInARow: 7,
    rowsCount: [4, 5, 6, 7, 6, 5],
    leftOperands: [
      { a: 0, x: 0, c: 0, y: 0, b: 7 },
      { a: 0, x: 0, c: 0, y: 0, b: 9 },
      { a: 0, x: 0, c: 0, y: 0, b: 11 },
    ],
    rightOperands: [
      { a: 0, x: 0, c: 0, y: 0, b: 1 },
      { a: 0, x: 0, c: 0, y: 0, b: 2 },
      { a: 0, x: 0, c: 0, y: 0, b: 3 },
      { a: 0, x: 0, c: 0, y: 0, b: 4 },
      { a: 0, x: 0, c: 0, y: 0, b: 5 },
      { a: 0, x: 0, c: 0, y: 0, b: 6 },
      { a: 0, x: 0, c: 0, y: 0, b: 7 },
      { a: 0, x: 0, c: 0, y: 0, b: 8 },
      { a: 0, x: 0, c: 0, y: 0, b: 9 },
      { a: 0, x: 0, c: 0, y: 0, b: 10 },
      { a: 0, x: 0, c: 0, y: 0, b: 11 },
      { a: 0, x: 0, c: 0, y: 0, b: 12 },
    ],
    hasDuplicate: false,
    hasHint: false,
    howToWin: 'Get 4 in a row by multiplying',
    hasWorkingBar: false,
    name: '7,9,11 x 1-12',
    leftOperandRowButtonCount: 3,
    operation: GAME_OPERATION_TYPE.MULTIPLICATION,
  },
};

export const enum SERVER_ERROR_CODES {
  EMAIL_ALREADY_IN_USE = 'EMAIL_ALREADY_IN_USE',
  SIGN_UP_USER_TYPE_REQUIRED = 'USER_TYPE_REQUIRED',
  INVALID_ACCESS_CODE = 'INVALID_ACCESS_CODE',
  CLIENT_DATA_MISMATCH_ERROR = 'CLIENT_DATA_MISMATCH_ERROR',
  INVALID_ACCESS_TOKEN = 'INVALID_ACCESS_TOKEN',
  INVALID_REFRESH_TOKEN = 'INVALID_REFRESH_TOKEN',
  EXPIRED_ACCESS_TOKEN = 'EXPIRED_ACCESS_TOKEN',
  EXPIRED_PASSWORD_RECOVERY_TOKEN = 'EXPIRED_PASSWORD_RECOVERY_TOKEN',
  INVALID_ACCESS_TOKEN_REDIRECT = 'INVALID_ACCESS_TOKEN_REDIRECT',
  INVALID_PASSWORD = 'INVALID_PASSWORD',
  GENERAL_ERROR = 'GENERAL_ERROR',
  GOOGLE_LOGIN_FAILED = 'GOOGLE_LOGIN_FAILED',
  APPLE_LOGIN_FAILED = 'APPLE_LOGIN_FAILED',
  INVALID_SIGN_UP_CODE = 'INVALID_SIGN_UP_CODE',
  SIGN_UP_CODE_EXPIRED = 'SIGN_UP_CODE_EXPIRED',
  NOT_FOUND = 'NOT_FOUND',
  INVALID_PARAMETER = 'INVALID_PARAMETER',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  LIMIT_FILE_SIZE = 'LIMIT_FILE_SIZE',
  IMAGE_WITH_GIVEN_NAME_ALREADY_EXISTS = 'IMAGE_WITH_GIVEN_NAME_ALREADY_EXISTS',
  CATEGORY_WITH_GIVEN_NAME_ALREADY_EXISTS = 'CATEGORY_WITH_GIVEN_NAME_ALREADY_EXISTS',
  QUESTION_NOT_READY_ENABLE = 'QUESTION_NOT_READY_ENABLE',
  DUPLICATE_ANSWER_NAME = 'DUPLICATE_ANSWER_NAME',
  VIDEO_IS_PROCESSING = 'VIDEO_IS_PROCESSING',
  IMAGE_USED_ON_ANSWER = 'IMAGE_USED_ON_ANSWER',
  VIDEO_USED_ON_ANSWER = 'VIDEO_USED_ON_ANSWER',
  CANNOT_ENABLE_CATEGORY = 'CANNOT_ENABLE_CATEGORY',
}

export enum USER_QUERY_KEYS {
  getCurrentUsertData = 'getCurrentUserData',
  getCategoryForHomePage = 'getCategoryForHomePage',
}

export enum PaymentStatus {
  NotPaid = 'NotPaid',
  Pending = 'Pending',
  Active = 'Active',
  Canceled = 'Canceled',
  PaymentFailed = 'PaymentFailed',
}

export enum HiveSize {
  SingleUser = 'SingleUser',
  FamilyHive = 'FamilyHive',
  ClassHive = 'ClassHive',
  SchoolHive = 'SchoolHive',
}

export enum HiveSubscriptionType {
  month = 'month',
  year = 'year',
}

export const sizeTitle = {
  [HiveSize.ClassHive]: 'Class',
  [HiveSize.FamilyHive]: 'Group',
  [HiveSize.SingleUser]: 'Single',
  // [HiveSize.SchoolHive]: 'School Hive',
};

export interface DEFAULT_OPERAND {
  leftOperand: OPERAND_TYPE;
  rightOperand: OPERAND_TYPE;
}

export const operandIcon: Record<GAME_OPERATION_TYPE, any> = {
  [GAME_OPERATION_TYPE.ADDITION]: PlusIconV2,
  [GAME_OPERATION_TYPE.SUBTRACTION]: MinusIconV2,
  [GAME_OPERATION_TYPE.MULTIPLICATION]: XIconV2,
  [GAME_OPERATION_TYPE.DIVISION]: DivideIconV2,
};

export const backgroundOperationImages: Record<GAME_OPERATION_TYPE, any> = {
  [GAME_OPERATION_TYPE.ADDITION]: PlusBackground,
  [GAME_OPERATION_TYPE.SUBTRACTION]: MinusBackground,
  [GAME_OPERATION_TYPE.MULTIPLICATION]: MultiplyBackground,
  [GAME_OPERATION_TYPE.DIVISION]: DivideBackground,
};

export const operandText: Record<GAME_OPERATION_TYPE, string> = {
  [GAME_OPERATION_TYPE.ADDITION]: '+',
  [GAME_OPERATION_TYPE.SUBTRACTION]: '-',
  [GAME_OPERATION_TYPE.MULTIPLICATION]: 'x',
  [GAME_OPERATION_TYPE.DIVISION]: '÷',
};
